/**
 * Popsigns Wishlist — custom, theme-native wishlist.
 * Replaces Swym. Server-backed (Lambda + DynamoDB) with localStorage cache.
 *
 * Identity: window.PopsignsWishlist = { apiBase, customerId, email, isLoggedIn }
 *   ownerId = `customer-<id>` when logged in, else a persisted `anon-<uuid>`.
 *   Guests can save name+email (identify) or create an account (merge on login).
 *
 * Surfaces: <wishlist-toggle> heart on cards + PDP, header count bubble,
 *   slide-out drawer, /pages/wishlist grid, and a guest save prompt.
 *
 * Console prefix: [Wishlist]
 */
(() => {
  'use strict';

  const CFG = window.PopsignsWishlist || {};
  const API = (CFG.apiBase || '').replace(/\/$/, '');
  const SHOP_DOMAIN = 'popsigns.co';
  const LS_OWNER = 'popsigns_wishlist_owner';
  const LS_CACHE = 'popsigns_wishlist_cache';
  const LS_PROMPTED = 'popsigns_wishlist_prompted';
  const DEBUG = false;
  const log = (...a) => DEBUG && console.log('[Wishlist]', ...a);

  const HEART_SVG =
    '<svg class="wishlist-heart" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke-linejoin="round"/>' +
    '</svg>';
  // Tag (price-watch strip) + down-arrow (per-item drop badge).
  const TAG_SVG =
    '<svg viewBox="0 0 256 256" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M243.31,136l-99.4-99.41a16,16,0,0,0-11.32-4.69H40a8,8,0,0,0-8,8v92.69a16,16,0,0,0,4.69,11.31l99.4,99.41a16,16,0,0,0,22.63,0l84.59-84.69a16,16,0,0,0,0-22.62ZM132.69,224h0L40,131.31V48h83.31L216,140.69ZM84,80a12,12,0,1,1-12,12A12,12,0,0,1,84,80Z"/></svg>';
  const DROP_ARROW_SVG =
    '<svg viewBox="0 0 256 256" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M205.66,149.66l-72,72a8,8,0,0,1-11.32,0l-72-72a8,8,0,0,1,11.32-11.32L120,196.69V40a8,8,0,0,1,16,0V196.69l58.34-58.35a8,8,0,0,1,11.32,11.32Z"/></svg>';
  // Solid pencil — matches the cart's Edit design link (.enhanced-edit-btn).
  const PENCIL_SVG =
    '<svg viewBox="0 0 256 256" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M227.31,73.37,182.63,28.69a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H92.69A15.86,15.86,0,0,0,104,219.31L227.31,96a16,16,0,0,0,0-22.63Z"/></svg>';

  let ownerId = null;
  let items = [];
  let drawerEl = null;
  const productCache = {};

  // ---------- helpers ----------
  const money = (cents) =>
    cents == null ? '' : '$' + (Number(cents) / 100).toFixed(2).replace(/\.00$/, '');
  const pctOff = (price, compareAt) =>
    compareAt && price && compareAt > price ? Math.round((1 - price / compareAt) * 100) : 0;

  // Price-watch drop %: live price vs the reference — the price captured when the
  // item was SAVED (saved_price, immutable), or Shopify compare-at for items saved
  // before saved_price existed. 0 if it hasn't dropped. (Both need a fresh price,
  // so the drawer enriches all items on open before rendering badges — see openDrawer.)
  const priceDrop = (it) => {
    const cur = it && typeof it.price === 'number' ? it.price : null;
    if (cur == null) return 0;
    const ref = (typeof it.saved_price === 'number' && it.saved_price > cur) ? it.saved_price
              : (typeof it.compareAt === 'number' && it.compareAt > cur) ? it.compareAt : null;
    return ref ? Math.round((1 - cur / ref) * 100) : 0;
  };
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );

  const anonId = () => {
    let id;
    try { id = localStorage.getItem(LS_OWNER); } catch (e) {}
    if (!id) {
      id = 'anon-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      try { localStorage.setItem(LS_OWNER, id); } catch (e) {}
    }
    return id;
  };

  const resolveOwner = () =>
    CFG.isLoggedIn && CFG.customerId ? 'customer-' + CFG.customerId : anonId();

  const loadCache = () => {
    try { items = JSON.parse(localStorage.getItem(LS_CACHE) || '[]'); } catch (e) { items = []; }
  };
  const saveCache = () => {
    try { localStorage.setItem(LS_CACHE, JSON.stringify(items)); } catch (e) {}
  };

  const api = async (path, opts = {}) => {
    const res = await fetch(API + path, {
      method: opts.method || 'GET',
      headers: { 'Content-Type': 'application/json', 'X-Shop-Domain': SHOP_DOMAIN },
      body: opts.body ? JSON.stringify(opts.body) : undefined
    });
    if (!res.ok) throw new Error('wishlist api ' + res.status);
    return res.json();
  };

  const vkey = (v) => String(v || '0');
  const has = (productId, variantId) =>
    items.some((i) => String(i.productId) === String(productId) && vkey(i.variantId) === vkey(variantId));

  // The EX Show Variants on Collection app only rewrites each tile's link per
  // variant (not the toggle's data-variant-id), so read the variant from the
  // card's ?variant= link when inside a card.
  function elVariant(el) {
    const scope = el.closest('.card-wrapper');
    const link = scope && scope.querySelector('a[href*="variant="]');
    const m = link && link.getAttribute('href').match(/[?&]variant=(\d+)/);
    return m ? m[1] : (el.getAttribute('data-variant-id') || null);
  }

  // ---------- server sync ----------
  async function fetchServer() {
    if (!API) return;
    try {
      const d = await api('/api/wishlist?ownerId=' + encodeURIComponent(ownerId));
      items = d.items || [];
      saveCache();
      syncAll();
    } catch (e) { log('fetch fail', e); }
  }

  async function maybeMerge() {
    if (!(CFG.isLoggedIn && CFG.customerId) || !API) return;
    let anon;
    try { anon = localStorage.getItem(LS_OWNER); } catch (e) {}
    if (anon && anon.indexOf('anon-') === 0) {
      try {
        const d = await api('/api/wishlist/merge', {
          method: 'POST',
          body: { fromOwnerId: anon, toOwnerId: ownerId, customerId: String(CFG.customerId), email: CFG.email || null }
        });
        try { localStorage.removeItem(LS_OWNER); } catch (e) {}
        if (d.items) { items = d.items; saveCache(); syncAll(); }
      } catch (e) { log('merge fail', e); }
    }
  }

  // The card/tile only exposes the default ($) variant price + a partial label.
  // Fetch the product JSON and pull the saved variant's FULL title + real price
  // (e.g. the 20" Classic variant, not the 12" mini) so the wishlist matches cart.
  // Pick the design-radio collection (shape) that matches the variant's options.
  function pickShapeCollection(v, collections) {
    const keys = Object.keys(collections || {});
    if (!keys.length) return null;
    const hay = ((v.options || []).join(' ') + ' ' + (v.title || '')).toLowerCase();
    for (const k of keys) { if (hay.indexOf(k.toLowerCase()) !== -1) return k; }
    return keys[0];
  }

  async function enrichVariant(it) {
    if (!it.productHandle || !it.variantId) return;
    // pp_custom items already carry an accurate title/price/preview captured from
    // the cart line; don't overwrite the design preview with the product image.
    if (it.mode === 'pp_custom') return;
    try {
      if (!productCache[it.productHandle]) {
        productCache[it.productHandle] = fetch('/products/' + it.productHandle + '.js').then((r) => r.json());
      }
      const data = await productCache[it.productHandle];
      const v = (data.variants || []).find((x) => String(x.id) === String(it.variantId));
      if (v) {
        it.variantTitle = v.title && v.title !== 'Default Title' ? v.title : null;
        if (typeof v.price === 'number') it.price = v.price;
        // Live compare-at — drives the discounted-price strikethrough + the
        // price-watch fallback reference (for items saved before saved_price existed).
        it.compareAt = (typeof v.compare_at_price === 'number' && v.compare_at_price > it.price) ? v.compare_at_price : null;
        if (v.featured_image && v.featured_image.src) it.image = v.featured_image.src;
        // Pre-design: page (split_design_index) is shape-independent; pick the
        // collection by the variant's shape, then build the verified S3 preview.
        if (it.mode === 'predesigned' && it.ppPage && it.ppCollections) {
          const key = pickShapeCollection(v, it.ppCollections);
          const coll = key && it.ppCollections[key];
          if (coll) {
            it.ppCollection = coll;
            it.ppPreview = 'https://s3-eu-west-1.amazonaws.com/pitchprint.io/previews/' + coll + '_' + it.ppPage + '.jpg';
          }
        }
      }
    } catch (e) { log('enrich fail', e); }
  }

  async function add(p, opts) {
    if (has(p.productId, p.variantId)) return;
    const stored = Object.assign({ wishlistItemId: ownerId + '::' + p.productId + '::' + vkey(p.variantId) }, p);
    items.unshift(stored);
    // save-for-later passes { silent:true } — the confirm strip is the feedback there,
    // so we skip the generic toast to avoid a duplicate "Saved to your wishlist".
    saveCache(); syncAll(); if (!(opts && opts.silent)) toast('Saved to your wishlist');
    await enrichVariant(stored);
    // Snapshot the price at save time (immutable) so the price-watch can detect a
    // later drop. enrichVariant has resolved the accurate variant price by now.
    if (stored.saved_price == null && typeof stored.price === 'number') stored.saved_price = stored.price;
    saveCache(); syncAll();
    const body = Object.assign({ ownerId }, stored);
    delete body.wishlistItemId;
    delete body.ppCollections; // transient client-side helper; not in the API schema
    try { await api('/api/wishlist', { method: 'POST', body }); }
    catch (e) { log('add fail', e); }
    maybePrompt();
  }

  async function remove(productId, variantId) {
    items = items.filter((i) => !(String(i.productId) === String(productId) && vkey(i.variantId) === vkey(variantId)));
    saveCache(); syncAll();
    const wid = ownerId + '::' + productId + '::' + vkey(variantId);
    try {
      await api(
        '/api/wishlist/' + encodeURIComponent(wid) + '?ownerId=' + encodeURIComponent(ownerId),
        { method: 'DELETE' }
      );
    } catch (e) { log('remove fail', e); }
  }

  const toggle = (p) => (has(p.productId, p.variantId) ? remove(p.productId, p.variantId) : add(p));

  // ---------- add to cart ----------
  async function addToCart(variantId, item) {
    if (!variantId) { toast('Choose options on the product page', true); return; }
    const line = { id: Number(variantId), quantity: 1 };
    // Pre-designed (namesake/split) products carry their default PitchPrint
    // design "as shown" so the cart line + order have valid artwork.
    if (item && item.mode === 'predesigned' && item.ppCollection && item.ppPreview) {
      // Attach the exact design reference (collection + page, shape-resolved) +
      // the verified S3 preview. The mini-cart PitchPrint component renders the
      // preview natively (both sides via the order-bump fallback); downstream
      // order processing has collection+page to produce the print-ready file.
      line.properties = {
        Artwork: '✓ Pre-Designed (as shown)',
        _pp_predesigned: '1',
        _pp_design_collection: item.ppCollection,
        _pp_design_page: String(item.ppPage),
        _pp_design_preview: item.ppPreview,
        // Full-resolution, print-ready artwork for fulfillment (Zapier / order-
        // file-processing). Resolves to the single-page print PDF for this exact
        // design — the endpoint renders the PitchPrint collection, extracts page
        // `_pp_design_page`, caches it on S3, and 302-redirects to the file.
        _pp_print_pdf: API + '/api/predesigned-artwork/' + item.ppCollection + '/' + item.ppPage
      };
    } else if (item && item.cartProperties) {
      // A line whose properties were captured at save time — a customized design
      // (PitchPrint project id, file URLs, previews, sides) OR a quick-add/bump line
      // carrying just an Artwork status property. Replay ALL captured properties
      // verbatim + a provenance marker so the artwork status badge is preserved —
      // same approach as reorder-with-artwork.
      line.properties = Object.assign({}, item.cartProperties, { _wishlist_restored: '1' });
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [line] })
      });
      if (!res.ok) throw new Error('cart ' + res.status);
      // Move semantics: remove from the wishlist, close it, refresh + open cart.
      // Must pass variantId — the wishlist is variant-keyed, so remove(productId)
      // alone keys on variant '0' and never matches (item stayed in the list).
      if (item && item.productId) await remove(item.productId, item.variantId);
      closeDrawer();
      toast('Moved to cart');
      await refreshCart();
    } catch (e) { toast('Could not add to cart', true); log('cart fail', e); }
  }

  // Mirror cart-offer-engine.js refreshCartSections(): re-render the cart
  // sections, fire cart:updated (so mini-cart PitchPrint previews + enhanced
  // item styling re-process), then open the drawer.
  async function refreshCart() {
    const sectionIds = ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
    try {
      const res = await fetch(location.pathname + '?sections=' + sectionIds.join(','));
      const sections = await res.json();
      for (const sectionId of Object.keys(sections)) {
        const el = document.getElementById(sectionId);
        if (!el) continue;
        const parsed = new DOMParser().parseFromString(sections[sectionId], 'text/html');
        const fresh = parsed.querySelector('.shopify-section') || parsed.body.firstChild;
        if (fresh) el.innerHTML = fresh.innerHTML;
      }
      if (window.debugMiniCartPreviews && window.debugMiniCartPreviews.clearProcessed) {
        window.debugMiniCartPreviews.clearProcessed();
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) { log('cart refresh fail', e); }
    // Keep the drawer open after a refresh — but never let open() (which dereferences the
    // drawer's <details>) throw out of refreshCart: a throw here would skip whatever the
    // caller does next (e.g. injecting the save-for-later confirm strip).
    try {
      const miniCart = document.querySelector('mini-cart');
      if (miniCart && typeof miniCart.open === 'function') miniCart.open();
    } catch (e) { log('mini-cart reopen after refresh failed (non-fatal):', e); }
  }

  // ---------- save for later (cart → wishlist) ----------
  // A cart line carries a reproducible CUSTOM design (PitchPrint project,
  // uploaded file, design-radio pick, or bundle logo) when it has any of these
  // keys. Such a line is fully reproducible by replaying its line-item
  // properties verbatim — mirrors the reorder-with-artwork checkout extension's
  // hasDesignProperties().
  const DESIGN_KEYS = ['_pitchprint', '_front_file_url', '_front_file_cropped_url', '_ppDesignRadioSelected', '_bundleLogoUrl'];
  const hasDesignProps = (props) => DESIGN_KEYS.some((k) => props[k]);
  // Best front-preview URL for the wishlist thumbnail (same fallback order the
  // reorder extension uses, plus the theme's own keys as extra fallbacks).
  const designFrontPreview = (props) =>
    props._pitchprint_preview_url || props._front_file_cropped_url ||
    props._file_upload_front_preview || props._front_file_url ||
    props._preview_url || props._preview || props._pp_design_preview || null;

  // Build a wishlist item from a /cart.js line. Pre-designed lines carry their
  // collection/page/preview so the item re-adds "as shown". A customized line
  // (PitchPrint / file-upload / design-radio / bundle-logo) is saved as
  // 'pp_custom' with ALL its non-empty properties captured, so the exact design
  // (project id, file URLs, previews) replays verbatim on re-add.
  function buildItemFromCartLine(line) {
    const props = line.properties || {};
    let mode = 'simple';
    let cartProperties = null;
    let designImg = null;
    if (props._pp_predesigned) {
      mode = 'predesigned';
    } else if (hasDesignProps(props)) {
      mode = 'pp_custom';
      cartProperties = {};
      Object.keys(props).forEach((k) => {
        if (props[k] != null && props[k] !== '') cartProperties[k] = String(props[k]);
      });
      designImg = designFrontPreview(props);
    } else if (props.Artwork || props._artwork_later) {
      // Upsell-bump / quick-add (and PDP "send artwork later") lines carry an Artwork
      // status property that drives the cart badge but have NO PitchPrint/file design
      // keys, so they'd otherwise fall through to 'simple' and lose the property —
      // dropping the badge when re-added from the wishlist. Capture all non-empty props
      // so the badge survives the save-for-later → re-add roundtrip (replayed verbatim in
      // addToCart). Mode stays 'simple' — a normal product; enrichVariant may refresh
      // title/price (it never touches cartProperties).
      cartProperties = {};
      Object.keys(props).forEach((k) => {
        if (props[k] != null && props[k] !== '') cartProperties[k] = String(props[k]);
      });
    }
    return {
      productId: line.product_id,
      variantId: String(line.variant_id),
      variantTitle: line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : null,
      productHandle: line.handle || null,
      productTitle: line.product_title || line.title || null,
      image: designImg || (line.featured_image && line.featured_image.url) || line.image || null,
      price: line.final_price != null ? line.final_price : line.price,
      compareAt: null,
      mode: mode,
      ppPage: props._pp_design_page || null,
      ppCollection: props._pp_design_collection || null,
      ppPreview: props._pp_design_preview || null,
      cartProperties: cartProperties
    };
  }

  function cartLineIndex(el) {
    // mini-cart: <cart-remove-button data-index>; main-cart: <tr id="CartItem-N">
    const withIdx = el.querySelector('[data-index]');
    if (withIdx) return parseInt(withIdx.getAttribute('data-index'), 10) || null;
    const m = (el.id || '').match(/CartItem-(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  }

  // Optimistically adjust the item-COUNT labels (header "· N items" + "Subtotal (N items)")
  // by `delta`. Per the build ticket (B4) only the counts react instantly; dollar Total
  // re-syncs from the real cart on refresh. refreshCart() re-renders these, so this is just
  // for the ~network gap — and any mis-parse self-corrects on the next refresh.
  function adjustCartCountLabels(delta) {
    document.querySelectorAll('.mini-cart__count').forEach((el) => {
      const cur = parseInt((el.textContent.match(/\d+/) || ['0'])[0], 10);
      const n = Math.max(0, cur + delta);
      el.innerHTML = '&nbsp;&middot; ' + n + ' ' + (n === 1 ? 'item' : 'items');
    });
    document.querySelectorAll('.cart-order-summary__label').forEach((el) => {
      const t = el.textContent || '';
      if (!/^Subtotal/.test(t)) return;
      const cur = parseInt((t.match(/\((\d+)/) || ['', '0'])[1], 10);
      const n = Math.max(0, cur + delta);
      el.textContent = 'Subtotal (' + n + ' ' + (n === 1 ? 'item' : 'items') + ')';
    });
  }

  async function saveLineForLater(lineEl, btn) {
    const lineIndex = cartLineIndex(lineEl);
    if (!lineIndex) { toast('Could not save this item', true); return; }
    if (lineEl.classList.contains('cart-item--saving')) return; // guard double-click
    const isRow = lineEl.tagName === 'TR';
    btn.disabled = true;
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const line = cart.items[lineIndex - 1];
      if (!line) throw new Error('line not found');
      // Authoritative guard: never save/remove a free gift or a synthetic fee
      // line (the GWP re-adds itself on threshold; fees aren't real products).
      if (line.properties && (line.properties._auto_gift || line.properties._processing_fee)) {
        btn.disabled = false; return;
      }
      const item = buildItemFromCartLine(line);
      // Capture an undo payload (re-add the EXACT variant + qty + properties) before removal.
      const undoPayload = { id: line.variant_id, quantity: line.quantity, properties: line.properties || {} };

      // Main cart page (<tr>): reload to re-render the table (no body strip / collapse there).
      if (isRow) {
        try { if (!has(item.productId, item.variantId)) await add(item, { silent: true }); }
        catch (err) { log('wishlist add during save-for-later failed (non-fatal):', err); }
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ line: lineIndex, quantity: 0 })
        });
        toast('Saved for later'); location.reload(); return;
      }

      // Drawer: optimistic count, collapse the line, then the IN-PLACE confirm strip.
      // IMPORTANT: do NOT refreshCart() here. Re-rendering the mini-cart section triggers the
      // drawer's own open/lazy-load lifecycle, which collapses/re-fetches the section and wipes
      // a freshly-injected strip (the strip was landing display:none → 0×0). Instead we replace
      // the line within the already-open drawer and reconcile values in-JS; a full refresh only
      // happens on dismiss/Undo/next mutation.
      adjustCartCountLabels(-line.quantity);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Is this the last real line? If so, save → empty cart: refresh to show the empty state
      // and use the body-level fallback strip (no list to replace in place).
      const realSiblings = lineEl.parentNode
        ? lineEl.parentNode.querySelectorAll(':scope > li[data-variant-id]') : [];
      const isLast = realSiblings.length <= 1;
      lineEl.classList.add('cart-item--saving');
      const work = (async () => {
        // Resilient: add() awaits variant enrichment, which can throw — a wishlist/enrichment
        // hiccup must NOT abort the cart removal or the strip (the item is already in the
        // wishlist cache by then). The cart change is the gating op; if THAT rejects we fall
        // to catch and don't claim "saved".
        try { if (!has(item.productId, item.variantId)) await add(item, { silent: true }); }
        catch (err) { log('wishlist add during save-for-later failed (non-fatal):', err); }
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ line: lineIndex, quantity: 0 })
        });
      })();
      // Wait for BOTH the network and the collapse (whichever is slower).
      const collapseWait = reduce ? Promise.resolve() : new Promise((r) => setTimeout(r, 420));
      await Promise.all([work, collapseWait]);
      if (isLast) {
        // Cart now empty — refresh to render the empty state, then the body-level strip.
        await refreshCart();
        injectSaveForLater();
        showSflConfirm(item, undoPayload);
      } else {
        // Replace the collapsed line with the strip in place (no section refresh), then
        // reconcile the dollar subtotal/total in-JS and nudge the bar/offers to update.
        showSflInPlace(lineEl, item, undoPayload);
        reconcileSummaryValues();
        try { document.dispatchEvent(new CustomEvent('cart:updated')); } catch (_) {}
      }
    } catch (e) {
      // Failure: re-sync the true cart (restores the line + counts), surface the error.
      lineEl.classList.remove('cart-item--saving');
      try { await refreshCart(); injectSaveForLater(); } catch (_) {}
      btn.disabled = false;
      toast('Could not save this item', true);
      log('save-for-later fail', e);
    }
  }

  let sflConfirmTimer = null;

  // Wire the shared dismissal timer (8s, paused on hover/focus) + Undo behavior onto a
  // confirm strip (used by both the in-place and the body-level fallback).
  function wireSflStrip(strip, undoBtn, item, undoPayload, onDismiss) {
    const arm = () => { clearTimeout(sflConfirmTimer); sflConfirmTimer = setTimeout(onDismiss, 8000); };
    arm();
    strip.onmouseenter = () => clearTimeout(sflConfirmTimer);
    strip.onmouseleave = arm;
    undoBtn.onfocus = () => clearTimeout(sflConfirmTimer);
    undoBtn.onblur = arm;
    try { undoBtn.focus({ preventScroll: true }); } catch (_) {} // a11y (B7): focus moves to Undo
    undoBtn.onclick = async () => {
      clearTimeout(sflConfirmTimer);
      undoBtn.disabled = true;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [undoPayload] })
        });
        await remove(item.productId, item.variantId);
        await refreshCart();
        injectSaveForLater();
        onDismiss(); // hide body strip / clean up the (now re-rendered) in-place strip
      } catch (e) {
        // Keep the strip; shake + danger border, leave Undo retryable (B6).
        log('sfl undo fail', e);
        strip.classList.add(strip.classList.contains('cart-saved-row') ? 'cart-saved-row--error' : 'wishlist-sfl-confirm--error');
        undoBtn.disabled = false;
        arm();
      }
    };
  }

  // In-place confirm strip (build ticket B2/B3): replaces the saved line, IN the live drawer
  // (no section refresh — that re-render would collapse/wipe it). lineEl is the collapsing line.
  function showSflInPlace(lineEl, item, undoPayload) {
    const existing = document.querySelector('.cart-saved-row-li');
    if (existing) existing.remove();
    const li = document.createElement('li');
    li.className = 'cart-saved-row-li';
    li.innerHTML =
      '<div class="cart-saved-row" role="status" aria-live="polite">' +
        '<span class="cart-saved-row__icon">' + HEART_SVG + '</span>' +
        '<div class="cart-saved-row__text"><strong>Saved to your wishlist</strong>' +
        '<span>' + esc(item.productTitle || 'Item') + ' moved out of your cart</span></div>' +
        '<button type="button" class="cart-saved-row__undo" data-undo-save aria-label="Undo — move ' + esc(item.productTitle || 'item') + ' back to your cart">Undo</button>' +
      '</div>';
    if (lineEl && lineEl.parentNode) { lineEl.parentNode.insertBefore(li, lineEl); lineEl.remove(); }
    else { document.body.appendChild(li); } // shouldn't happen; keep the strip reachable
    wireSflStrip(li.querySelector('.cart-saved-row'), li.querySelector('[data-undo-save]'), item, undoPayload, () => dismissSflInPlace(li));
    return true;
  }
  // Timeout/dismiss: animate out + remove. No refresh needed — the line was already removed
  // server-side and the drawer already shows the remaining lines + reconciled values.
  function dismissSflInPlace(li) {
    const strip = li.querySelector('.cart-saved-row');
    if (strip) { strip.classList.add('cart-saved-row--out'); setTimeout(() => li.remove(), 200); }
    else li.remove();
  }

  // Update the drawer's dollar Subtotal + Total in place from a fresh cart (no section refresh,
  // which would wipe the in-place strip). Counts are already handled by adjustCartCountLabels.
  async function reconcileSummaryValues() {
    try {
      const c = await fetch('/cart.js').then((r) => r.json());
      const money = (cents) => '$' + (cents / 100).toFixed(2) + (c.currency ? ' ' + c.currency : '');
      const totalEl = document.querySelector('.cart-order-summary__row--total .cart-order-summary__value');
      if (totalEl) totalEl.textContent = money(c.total_price);
      let sub = 0;
      for (const it of c.items) {
        if (it.properties && (it.properties._auto_gift || it.properties._processing_fee)) continue;
        sub += it.final_line_price || 0;
      }
      const subEl = document.getElementById('mini-cart-subtotal');
      if (subEl) subEl.textContent = money(sub);
    } catch (e) { log('summary reconcile failed', e); }
  }

  // Body-level fallback strip (used only when the cart is now empty — no list to inject into).
  function showSflConfirm(item, undoPayload) {
    let strip = document.querySelector('.wishlist-sfl-confirm');
    if (!strip) {
      strip = document.createElement('div');
      strip.className = 'wishlist-sfl-confirm';
      strip.setAttribute('role', 'status');
      strip.setAttribute('aria-live', 'polite');
      document.body.appendChild(strip);
    }
    strip.classList.remove('wishlist-sfl-confirm--error');
    strip.innerHTML =
      '<div class="wishlist-sfl-confirm__body">' +
        '<span class="wishlist-sfl-confirm__heart">' + HEART_SVG + '</span>' +
        '<span class="wishlist-sfl-confirm__text"><strong>Saved to your wishlist</strong>' +
        '<span class="wishlist-sfl-confirm__sub">' + esc(item.productTitle || 'Item') + ' moved out of your cart</span></span>' +
      '</div>' +
      '<button type="button" class="wishlist-sfl-confirm__undo" data-undo-save aria-label="Undo — move ' + esc(item.productTitle || 'item') + ' back to your cart">Undo</button>';
    // Restart the entry + heart-pop animations even when the strip is reused.
    strip.classList.remove('is-visible');
    void strip.offsetWidth;
    strip.classList.add('is-visible');
    wireSflStrip(strip, strip.querySelector('[data-undo-save]'), item, undoPayload, hideSflConfirm);
  }
  function hideSflConfirm() {
    const strip = document.querySelector('.wishlist-sfl-confirm');
    if (strip) strip.classList.remove('is-visible');
  }

  // Inject a "Save for later" control into each cart line (idempotent). Auto-gift
  // and processing-fee lines must NOT get the control — but they render no DOM
  // marker (underscore-prefixed properties aren't output, and the mini-cart skips
  // them entirely while the main cart still renders the gift row). So we read the
  // line properties from /cart.js and skip by line index. (saveLineForLater also
  // guards authoritatively, in case this fetch fails.)
  const SFL_SEL = 'cart-items li[data-variant-id], cart-items tr[data-variant-id]';
  // Host = the enhanced action row's left group (so "Save for later" sits next to
  // "Edit design", per the redesign). The enhanced layer is built by
  // cart-offer-engine AFTER our first run, so for a not-yet-enhanced mini-cart
  // <li> we return null and wait (the observeCart MutationObserver re-fires once
  // the left group appears). Main cart <tr> has no enhanced row → its details cell.
  function sflHost(lineEl) {
    // /cart <tr> now has a Liquid-first .enhanced-left-group action row — share it with the edit
    // button (PARITY §2) instead of appending loose to the details cell.
    if (lineEl.tagName === 'TR') return lineEl.querySelector('.enhanced-left-group') || lineEl.querySelector('.cart-item__details') || lineEl;
    const lg = lineEl.querySelector('.enhanced-left-group');
    if (lg) return lg;
    if (lineEl.hasAttribute('data-enhanced')) return lineEl.querySelector('.product-content');
    return null; // pending enhancement — inject on a later tick
  }
  async function injectSaveForLater() {
    // Idempotency keyed on "does the host already hold the control?" (not a
    // line-level marker) so a host rebuilt by re-enhancement gets it re-injected.
    // Cheap synchronous pre-check; skip the fetch when nothing needs it.
    let any = false;
    document.querySelectorAll(SFL_SEL).forEach((l) => {
      const h = sflHost(l);
      if (h && !h.querySelector('.wishlist-save-later')) any = true;
    });
    if (!any) return;
    // Fetch FIRST, then re-query + inject synchronously (no await between the
    // query below and appendChild — a re-render mid-fetch detaches stale nodes).
    let cartItems = null;
    try { cartItems = (await fetch('/cart.js').then((r) => r.json())).items; } catch (e) { cartItems = null; }
    document.querySelectorAll(SFL_SEL).forEach((lineEl) => {
      const host = sflHost(lineEl);
      if (!host || host.querySelector('.wishlist-save-later')) return;
      const idx = cartLineIndex(lineEl);
      const props = (cartItems && idx && cartItems[idx - 1] && cartItems[idx - 1].properties) || {};
      if (props._auto_gift || props._processing_fee) return; // not saveable
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wishlist-save-later';
      // Use the same heart SVG as the nav/cards (not a unicode glyph) so the icon matches (PARITY §2.70).
      btn.innerHTML = HEART_SVG + '<span>Save for later</span>';
      // stopPropagation so cart-line delegates (e.g. the PitchPrint preview /
      // save-for-later redirect interceptors) don't also fire on this click.
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); saveLineForLater(lineEl, btn); });
      host.appendChild(btn);
    });
  }

  // Re-inject reliably: the cart sections re-render on every change (quantity,
  // remove, add, open), which wipes our injected buttons AND their bound markers.
  // A fixed setTimeout after one event is racy (the button appeared only "when I
  // toggled quantity"). Mirror cart-offer-engine's full trigger set so we re-run
  // whenever the lines (re)render, debounced.
  function observeCart() {
    let t = null;
    const ping = () => { clearTimeout(t); t = setTimeout(injectSaveForLater, 120); };
    // Our own + cart-offer-engine refreshes and the theme's other cart signals.
    ['cart:updated', 'cart:refresh', 'cart:item-added', 'cart:quantity-changed', 'cartdrawer:opened']
      .forEach((ev) => document.addEventListener(ev, ping));
    // Theme pub/sub bus — native quantity change / line removal go through here
    // (NOT the DOM cart:updated event), which is why the button was unreliable.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, ping);
      }
    } catch (e) {}
    if (window.PitchPrintStateManager) {
      try { window.PitchPrintStateManager.on('cart:updated', ping); } catch (e) {}
    }
    // Watch the line containers themselves: their innerHTML is replaced on every
    // cart re-render. The section wrappers (#mini-cart / #main-cart-items) persist,
    // so observing their subtree catches every line (re)render directly.
    ['mini-cart', 'main-cart-items'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) new MutationObserver(ping).observe(el, { childList: true, subtree: true });
    });
    // Mini-cart open/close (content may have been re-rendered while closed).
    const mc = document.querySelector('mini-cart');
    if (mc) new MutationObserver(ping).observe(mc, { attributes: true, attributeFilter: ['class', 'open', 'aria-hidden'] });
    // Resurrection safety net (proven pattern in this theme). injectSaveForLater
    // self-guards with a cheap synchronous pre-check and only hits /cart.js when a
    // line's host is actually missing the control, so steady state stays free.
    setInterval(injectSaveForLater, 700);
  }

  // ---------- UI sync ----------
  function syncAll() {
    document.querySelectorAll('wishlist-toggle').forEach((el) => {
      const on = has(el.getAttribute('data-product-id'), elVariant(el));
      el.classList.toggle('is-saved', on);
      const btn = el.querySelector('button');
      if (btn) {
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
      }
    });
    document.querySelectorAll('[data-wishlist-badge]').forEach((b) => {
      const countEl = b.querySelector('[data-wishlist-count]');
      if (countEl) countEl.textContent = items.length;
      b.classList.toggle('is-visible', items.length > 0);
    });
    renderDrawer();
    renderPage();
  }

  // ---------- PDP live-variant tracking ----------
  // The PDP <wishlist-toggle> is rendered with the SERVER-side default variant
  // (selected_or_first_available). When the shopper changes configurator
  // options, the theme's variant-selects updates the form + publishes
  // variantChange — but never touches our data-* attrs, so the heart kept
  // saving the wrong (default) variant. Mirror the live variant onto the PDP
  // toggle's attrs + re-sync its saved state (the wishlist is variant-keyed).
  function applyVariantToToggle(el, v) {
    if (!el || !v || el.closest('.card-wrapper')) return; // cards are handled via the ?variant= link
    el.setAttribute('data-variant-id', String(v.id));
    if (v.title) el.setAttribute('data-variant-title', v.title);
    if (typeof v.price === 'number') el.setAttribute('data-product-price', String(v.price));
    if (v.compare_at_price != null) el.setAttribute('data-product-compare', String(v.compare_at_price));
    else el.removeAttribute('data-product-compare');
    const img = v.featured_image && v.featured_image.src;
    if (img) el.setAttribute('data-product-image', img);
    const on = has(el.getAttribute('data-product-id'), v.id);
    el.classList.toggle('is-saved', on);
    const btn = el.querySelector('button');
    if (btn) {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
    }
  }

  function bindVariantTracking() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
      const v = evt && evt.data && evt.data.variant;
      if (!v) return;
      // Scope to the product section that fired the change (a page can host more
      // than one product); fall back to all PDP toggles if it can't be found.
      let root = document;
      const sid = evt.data.sectionId;
      if (sid) {
        const sec = document.getElementById('shopify-section-' + sid) || document.querySelector('[data-section="' + sid + '"]');
        if (sec) root = sec.closest('.shopify-section') || sec;
      }
      root.querySelectorAll('wishlist-toggle.wishlist-toggle--product').forEach((el) => applyVariantToToggle(el, v));
    });
  }

  // ---------- mode-aware primary CTA ----------
  function ctaHTML(it, url) {
    // pp_custom: a finished custom design captured from the cart — re-add it
    // exactly (replays the saved PitchPrint/file/preview properties), with an
    // "Edit design" link back to the product to start a fresh design.
    if (it.mode === 'pp_custom' && it.variantId) {
      return (
        '<button type="button" class="wishlist-btn wishlist-btn--cart" data-wishlist-add="' + esc(it.variantId) + '">Add to cart</button>' +
        '<a class="wishlist-link wishlist-edit" href="' + esc(url) + '">' + PENCIL_SVG + '<span>Edit design</span></a>'
      );
    }
    // customize: must design on the product page (generic/blank-canvas customizable)
    if (it.mode === 'customize') {
      return '<a class="wishlist-btn wishlist-btn--cart" href="' + esc(url) + '">Customize</a>';
    }
    if (!it.variantId) {
      return '<a class="wishlist-btn wishlist-btn--cart" href="' + esc(url) + '">View</a>';
    }
    // simple (direct) or predesigned (adds its default design "as shown")
    const pre = it.mode === 'predesigned' ? ' data-wishlist-predesign="1"' : '';
    let html = '<button type="button" class="wishlist-btn wishlist-btn--cart" data-wishlist-add="' + esc(it.variantId) + '"' + pre + '>Add to cart</button>';
    if (it.mode === 'predesigned') {
      html += '<a class="wishlist-link" href="' + esc(url) + '">Customize</a>';
    }
    return html;
  }

  // ---------- item card markup (shared by drawer + page) ----------
  function itemCardHTML(it, readOnly) {
    const off = pctOff(it.price, it.compareAt);
    const drop = priceDrop(it);
    const url = it.productHandle ? '/products/' + it.productHandle : '#';
    // Price cell — single price, or sale + strikethrough compare-at (cart parity).
    const priceHTML =
      (it.price != null ? '<span class="wishlist-item__now">' + money(it.price) + '</span>' : '') +
      (off ? ' <span class="wishlist-item__was">' + money(it.compareAt) + '</span>' : '');
    return (
      '<li class="wishlist-item" data-product-id="' + esc(it.productId) + '" data-variant-id="' + esc(it.variantId || '') + '">' +
      '<a class="wishlist-item__media" href="' + esc(url) + '">' +
      (it.image ? '<img src="' + esc(it.image) + '" alt="" loading="lazy">' : '<span class="wishlist-item__noimg"></span>') +
      (off ? '<span class="wishlist-item__badge">-' + off + '%</span>' : '') +
      '</a>' +
      '<div class="wishlist-item__info">' +
      // Title row: title (left) + price (right), mirroring the cart line item.
      '<div class="wishlist-item__head">' +
      '<a class="wishlist-item__title" href="' + esc(url) + '">' + esc(it.productTitle || 'View product') + '</a>' +
      '<span class="wishlist-item__price">' + priceHTML + '</span>' +
      '</div>' +
      (it.variantTitle ? '<div class="wishlist-item__variant">' + esc(it.variantTitle) + '</div>' : '') +
      // §5 per-item drop badge — only when the price actually fell since saved.
      (drop ? '<span class="wishlist-item__drop-badge">' + DROP_ARROW_SVG + 'Price dropped ' + drop + '% since you saved</span>' : '') +
      '<div class="wishlist-item__actions">' +
      ctaHTML(it, url) +
      (readOnly ? '' : '<button type="button" class="wishlist-link" data-wishlist-remove>Remove</button>') +
      '</div>' +
      '</div>' +
      '</li>'
    );
  }

  function listHTML(arr, readOnly) {
    if (!arr.length) {
      return '<div class="wishlist-empty"><p>Your wishlist is empty.</p><p class="wishlist-empty__hint">Tap the &#9825; on any product to save it here.</p></div>';
    }
    return '<ul class="wishlist-list">' + arr.map((it) => itemCardHTML(it, readOnly)).join('') + '</ul>';
  }

  function bindListActions(root, readOnly) {
    root.querySelectorAll('[data-wishlist-add]').forEach((b) =>
      b.addEventListener('click', () => {
        if (b.disabled) return;
        b.disabled = true;
        b.textContent = 'Moving…';
        const li = b.closest('[data-product-id]');
        const pid = li && li.getAttribute('data-product-id');
        const vid = li && li.getAttribute('data-variant-id');
        const item = items.find((i) => String(i.productId) === String(pid) && vkey(i.variantId) === vkey(vid));
        addToCart(b.getAttribute('data-wishlist-add'), item);
      })
    );
    if (!readOnly) {
      root.querySelectorAll('[data-wishlist-remove]').forEach((b) =>
        b.addEventListener('click', () => {
          const li = b.closest('[data-product-id]');
          remove(li.getAttribute('data-product-id'), li.getAttribute('data-variant-id'));
        })
      );
    }
  }

  // ---------- drawer ----------
  function buildDrawer() {
    drawerEl = document.createElement('div');
    drawerEl.className = 'wishlist-drawer';
    drawerEl.hidden = true;
    drawerEl.innerHTML =
      // ZWSP keeps the overlay non-empty: Be Yours' `div:empty{display:none}`
      // (base.css) would otherwise hide it, killing the dim + click-out target.
      '<div class="wishlist-drawer__overlay" data-wishlist-close>&#8203;</div>' +
      '<aside class="wishlist-drawer__panel" role="dialog" aria-label="Your wishlist" aria-modal="true">' +
      // Drag handle — mobile bottom-sheet affordance only (hidden ≥750px via CSS);
      // drag-to-close wired in bindSheetDrag(). A <span> (not <div>) so Be Yours'
      // base.css `div:empty{display:none}` (0,1,1) can't beat the display:block.
      '<span class="wishlist-drawer__drag-handle" aria-hidden="true"></span>' +
      '<header class="wishlist-drawer__head">' +
      '<button class="wishlist-drawer__close" type="button" data-wishlist-close aria-label="Close wishlist">' +
      '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>' +
      '</button>' +
      '<div class="wishlist-drawer__title">Your Wishlist<span class="wishlist-drawer__count"></span></div>' +
      // §4 price-watch strip (the wishlist analogue of the cart threshold bar) —
      // populated + toggled in renderDrawer.
      '<div class="wishlist-watch" data-wishlist-watch hidden></div>' +
      '</header>' +
      '<div class="wishlist-drawer__body"></div>' +
      '<footer class="wishlist-drawer__foot"></footer>' +
      '</aside>';
    document.body.appendChild(drawerEl);
    drawerEl.querySelectorAll('[data-wishlist-close]').forEach((x) => x.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });
    bindSheetDrag();
  }

  // Mobile bottom-sheet drag-to-close (mirrors cart-offer-engine.js initSheetDrag):
  // drag the panel down by its handle past ~30% → closeDrawer(); snap back otherwise.
  function bindSheetDrag() {
    const handle = drawerEl && drawerEl.querySelector('.wishlist-drawer__drag-handle');
    const panel = drawerEl && drawerEl.querySelector('.wishlist-drawer__panel');
    if (!handle || !panel) return;
    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    let startY = 0, dy = 0, dragging = false;

    handle.addEventListener('touchstart', (e) => {
      if (!isMobile()) return;
      startY = e.touches[0].clientY;
      dy = 0;
      dragging = true;
      panel.style.transition = 'none';
      panel.style.animation = 'none';
    }, { passive: true });

    handle.addEventListener('touchmove', (e) => {
      if (!dragging) return;
      dy = e.touches[0].clientY - startY;
      if (dy < 0) dy = 0; // down only
      panel.style.transform = 'translateY(' + dy + 'px)';
    }, { passive: true });

    handle.addEventListener('touchend', () => {
      if (!dragging) return;
      dragging = false;
      const threshold = Math.min(140, (panel.offsetHeight || 600) * 0.3);
      if (dy > threshold) {
        panel.style.transition = 'transform var(--duration-long, 0.4s) ease';
        panel.style.transform = 'translateY(100%)';
        let done = false;
        const finalize = () => {
          if (done) return;
          done = true;
          closeDrawer();
          panel.style.transition = '';
          panel.style.transform = '';
          panel.style.animation = '';
        };
        panel.addEventListener('transitionend', function te(ev) {
          if (ev.propertyName !== 'transform') return;
          panel.removeEventListener('transitionend', te);
          finalize();
        });
        setTimeout(finalize, 500); // fallback if transitionend doesn't fire
      } else {
        panel.style.transition = 'transform 0.22s ease';
        panel.style.transform = 'translateY(0)';
        setTimeout(() => {
          if (!panel) return;
          panel.style.transition = '';
          panel.style.transform = '';
          panel.style.animation = '';
        }, 240);
      }
    }, { passive: true });
  }

  function renderDrawer() {
    if (!drawerEl) return;
    const body = drawerEl.querySelector('.wishlist-drawer__body');
    const foot = drawerEl.querySelector('.wishlist-drawer__foot');
    const count = drawerEl.querySelector('.wishlist-drawer__count');
    const watch = drawerEl.querySelector('[data-wishlist-watch]');
    if (count) count.textContent = items.length ? ' · ' + items.length + ' ' + (items.length === 1 ? 'item' : 'items') : '';
    // §4 price-watch strip — count items whose live price dropped vs their reference
    // and derive the headline % (the largest drop). Green clause only when ≥1 dropped.
    if (watch) {
      if (!items.length) {
        watch.hidden = true;
        watch.innerHTML = '';
      } else {
        const drops = items.map(priceDrop).filter((p) => p > 0);
        const maxPct = drops.reduce((m, p) => Math.max(m, p), 0);
        watch.innerHTML =
          '<span class="wishlist-watch__tile">' + TAG_SVG + '</span>' +
          '<span class="wishlist-watch__text">We watch your saved items for price drops &mdash; ' +
          (drops.length
            ? '<b class="wishlist-watch__hit">' + drops.length + ' just dropped ' + maxPct + '%</b>'
            : "we'll tell you the moment one does.") +
          '</span>';
        watch.hidden = false;
      }
    }
    body.innerHTML = listHTML(items, false);
    bindListActions(body, false);
    // "Add all" only covers items that add directly (exclude Customize-mode,
    // which must be designed on the product page first).
    const addable = items.filter((i) => i.variantId && i.mode !== 'customize');
    foot.innerHTML =
      items.length
        ? (addable.length ? '<button type="button" class="wishlist-btn wishlist-btn--primary" data-wishlist-addall>Add all to cart</button>' : '') +
          '<a class="wishlist-link wishlist-link--center" href="/pages/wishlist">View full wishlist &rarr;</a>' +
          // §6 saved-to-account reassurance — the wishlist analogue of the cart trust line.
          '<div class="wishlist-reassure">' + HEART_SVG + '<span>Saved to your account &mdash; come back anytime, on any device</span></div>'
        : '';
    const all = foot.querySelector('[data-wishlist-addall]');
    if (all) all.addEventListener('click', async () => { for (const it of addable) await addToCart(it.variantId, it); });
  }

  function openDrawer() {
    if (!drawerEl) buildDrawer();
    renderDrawer();
    drawerEl.hidden = false;
    // Mirror the theme's drawer behavior: measure the scrollbar so the page-
    // shift compensation (see CSS) has a width and the header doesn't jump.
    if (typeof window.setScrollbarWidth === 'function') window.setScrollbarWidth();
    document.body.classList.add('wishlist-drawer-open');
    // Refresh live prices in the background (the saved/persisted price is a stale
    // snapshot), then re-render so the price-watch strip + per-item drop badges
    // reflect the CURRENT prices.
    refreshPrices().then(() => { if (drawerEl && !drawerEl.hidden) renderDrawer(); });
  }
  function closeDrawer() { if (drawerEl) drawerEl.hidden = true; document.body.classList.remove('wishlist-drawer-open'); }

  // Refresh every item's live price (+ compareAt) so the price-watch drop math is
  // accurate — the persisted price is the save-time snapshot. enrichVariant caches
  // product.js per handle and skips pp_custom items (their captured price is final).
  async function refreshPrices() {
    if (!items.length) return;
    try { await Promise.all(items.map((it) => enrichVariant(it).catch(() => {}))); saveCache(); }
    catch (e) { log('price refresh fail', e); }
  }

  // ---------- full page ----------
  function renderPage() {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return;
    const sharedOwner = new URLSearchParams(location.search).get('owner');
    if (sharedOwner && sharedOwner !== ownerId) { renderSharedPage(root, sharedOwner); return; }
    root.innerHTML = listHTML(items, false);
    bindListActions(root, false);
    const tools = document.querySelector('[data-wishlist-tools]');
    if (tools) tools.style.display = items.length ? '' : 'none';
    // Refresh live prices then re-render so per-item drop badges are accurate here too.
    refreshPrices().then(() => {
      const cur = new URLSearchParams(location.search).get('owner');
      if (cur && cur !== ownerId) return; // shared view renders its own data
      if (!document.body.contains(root)) return;
      root.innerHTML = listHTML(items, false);
      bindListActions(root, false);
    });
  }

  async function renderSharedPage(root, owner) {
    root.innerHTML = '<div class="wishlist-empty"><p>Loading wishlist&hellip;</p></div>';
    try {
      const d = await api('/api/wishlist?ownerId=' + encodeURIComponent(owner));
      root.innerHTML = listHTML(d.items || [], true);
      bindListActions(root, true);
    } catch (e) { root.innerHTML = '<div class="wishlist-empty"><p>This wishlist could not be loaded.</p></div>'; }
    const tools = document.querySelector('[data-wishlist-tools]');
    if (tools) tools.style.display = 'none';
  }

  // ---------- guest save prompt ----------
  function maybePrompt() {
    if (CFG.isLoggedIn) return;
    let done;
    try { done = localStorage.getItem(LS_PROMPTED); } catch (e) {}
    if (done) return;
    showPrompt();
  }

  function showPrompt() {
    const wrap = document.createElement('div');
    wrap.className = 'wishlist-prompt';
    wrap.innerHTML =
      '<div class="wishlist-prompt__overlay" data-wp-close>&#8203;</div>' +
      '<div class="wishlist-prompt__panel" role="dialog" aria-label="Save your wishlist">' +
      '<button class="wishlist-prompt__close" type="button" data-wp-close aria-label="Close">&times;</button>' +
      '<h3>Save your wishlist &#127881;</h3>' +
      '<p>Add your name &amp; email to keep your wishlist on any device — and to be entered to <strong>win a wish</strong> during our 10-Year Birthday Sale.</p>' +
      '<form class="wishlist-prompt__form">' +
      '<input type="text" name="name" placeholder="First name" autocomplete="given-name">' +
      '<input type="email" name="email" placeholder="Email address" required autocomplete="email">' +
      '<button type="submit" class="wishlist-btn wishlist-btn--primary">Save my wishlist</button>' +
      '</form>' +
      '<a class="wishlist-link wishlist-link--center" href="/account/register">or create an account &rarr;</a>' +
      '</div>';
    document.body.appendChild(wrap);
    const close = () => { try { localStorage.setItem(LS_PROMPTED, '1'); } catch (e) {} wrap.remove(); };
    wrap.querySelectorAll('[data-wp-close]').forEach((x) => x.addEventListener('click', close));
    wrap.querySelector('form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const name = wrap.querySelector('[name=name]').value.trim();
      const email = wrap.querySelector('[name=email]').value.trim();
      if (!email) return;
      try {
        await api('/api/wishlist/identify', { method: 'POST', body: { ownerId, name: name || null, email } });
        if (window._learnq) window._learnq.push(['identify', { $email: email, $first_name: name }]);
        toast('Wishlist saved — good luck! &#127808;');
      } catch (err) { log('identify fail', err); }
      close();
    });
  }

  // ---------- toast ----------
  let toastTimer = null;
  function toast(msg, isError) {
    let t = document.querySelector('.wishlist-toast');
    if (!t) { t = document.createElement('div'); t.className = 'wishlist-toast'; document.body.appendChild(t); }
    t.innerHTML = msg;
    t.classList.toggle('is-error', !!isError);
    t.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('is-visible'), 2600);
  }

  // ---------- custom element ----------
  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      // De-dupe: the "EX Show Variants on Collection" app clones product cards
      // (and this heart). Keep only the first toggle per card-wrapper.
      const scope = this.closest('.card-wrapper');
      if (scope && scope.querySelector('wishlist-toggle') !== this) { this.remove(); return; }
      // Idempotent button (a cloned node carries the markup but no listener).
      let btn = this.querySelector('.wishlist-toggle__btn');
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wishlist-toggle__btn';
        btn.innerHTML = HEART_SVG;
        this.appendChild(btn);
      }
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', 'Save to wishlist');
      btn.onclick = (e) => { e.preventDefault(); e.stopPropagation(); toggle(this.data()); };
      this.classList.toggle('is-saved', has(this.getAttribute('data-product-id'), elVariant(this)));
    }
    data() {
      const num = (a) => { const v = this.getAttribute(a); return v ? parseInt(v, 10) : null; };
      const vt0 = this.getAttribute('data-variant-title');
      let variantId = this.getAttribute('data-variant-id') || null;
      let variantTitle = vt0 && vt0 !== 'Default Title' ? vt0 : null;
      let productTitle = this.getAttribute('data-product-title') || null;
      let image = this.getAttribute('data-product-image') || null;
      // The EX variant-split app only updates each tile's link + visible label +
      // image, not the toggle's data-*. Read the real variant/title/image from
      // the card DOM so each tile saves its own design.
      const scope = this.closest('.card-wrapper');
      if (scope) {
        const link = scope.querySelector('a[href*="variant="]');
        const m = link && link.getAttribute('href').match(/[?&]variant=(\d+)/);
        if (m) variantId = m[1];
        // Use the tile's displayed image (the EX app swaps it per shape).
        const img = scope.querySelector('.card__media img, .media img, img');
        const src = img && (img.currentSrc || img.getAttribute('src'));
        if (src) image = src;
      } else {
        // PDP: the configurator keeps the product form's hidden id input
        // authoritative for the selected variant. Trust it even if a
        // variantChange event was missed (belt-and-suspenders for data-variant-id).
        const sec = this.closest('product-info') || this.closest('.shopify-section') || this.closest('section');
        const idInput = sec && sec.querySelector('form[data-type="add-to-cart-form"] input[name="id"], input[name="id"]');
        if (idInput && idInput.value) variantId = idInput.value;
      }
      // variantTitle + accurate price + pre-design collection are filled by
      // enrichVariant() on add (needs the variant's shape).
      let ppCollections = null;
      try { ppCollections = JSON.parse(this.getAttribute('data-pp-collections') || 'null'); } catch (e) {}
      return {
        productId: num('data-product-id'),
        variantId: variantId,
        variantTitle: variantTitle,
        productHandle: this.getAttribute('data-product-handle') || null,
        productTitle: productTitle,
        image: image,
        price: num('data-product-price'),
        compareAt: num('data-product-compare'),
        mode: this.getAttribute('data-wishlist-mode') || 'simple',
        ppPage: this.getAttribute('data-pp-page') || null,
        ppCollections: ppCollections
      };
    }
  }
  if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggle);

  // ---------- header trigger ----------
  function bindHeaderTrigger() {
    document.querySelectorAll('[data-wishlist-trigger]').forEach((el) =>
      el.addEventListener('click', (e) => { e.preventDefault(); openDrawer(); })
    );
  }

  // ---------- share ----------
  function bindShare() {
    document.querySelectorAll('[data-wishlist-share]').forEach((el) =>
      el.addEventListener('click', async (e) => {
        e.preventDefault();
        const url = location.origin + '/pages/wishlist?owner=' + encodeURIComponent(ownerId);
        try {
          if (navigator.share) await navigator.share({ title: 'My Popsigns wishlist', url });
          else { await navigator.clipboard.writeText(url); toast('Wishlist link copied'); }
        } catch (err) { log('share', err); }
      })
    );
  }

  // ---------- init ----------
  function init() {
    ownerId = resolveOwner();
    loadCache();
    syncAll();
    bindHeaderTrigger();
    bindShare();
    injectSaveForLater();
    observeCart();
    bindVariantTracking();
    maybeMerge().then(fetchServer);
  }

  window.PopsignsWishlistAPI = { open: openDrawer, close: closeDrawer, toggle, add, remove, getItems: () => items.slice() };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
